import axios from '../../api/axios';
import { formatUsStateForPrint } from './usStateFormatting';

// ─── helpers ───────────────────────────────────────────────────────────────

const getApiBaseUrl = () =>
  window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
    ? 'https://baptistmenapi.onrender.com'
    : 'http://localhost:3001';

const REGION_MAP = {
  1:'Alamance – Region 4',2:'Alexander – Region 7',3:'Anson – Region 6',4:'Ashe – Region 7',
  5:'Avery – Region 7',6:'Beaufort – Region 2',7:'Bertie – Region 1',8:'Bladen – Region 3',
  9:'Brunswick – Region 3',10:'Buncombe – Region 9',11:'Burke – Region 8',12:'Cabarrus – Region 6',
  13:'Caldwell – Region 7',14:'Camden – Region 1',15:'Carteret – Region 2',16:'Caswell – Region 4',
  17:'Catawba – Region 8',18:'Chatham – Region 4',19:'Cherokee – Region 10',20:'Chowan – Region 1',
  21:'Clay – Region 10',22:'Cleveland – Region 8',23:'Columbus – Region 3',24:'Craven – Region 2',
  25:'Cumberland – Region 3',26:'Currituck – Region 1',27:'Dare – Region 1',28:'Davidson – Region 5',
  29:'Davie – Region 8',30:'Duplin – Region 3',31:'Durham – Region 4',32:'Edgecombe – Region 2',
  33:'Forsyth – Region 5',34:'Franklin – Region 4',35:'Gaston – Region 8',36:'Gates – Region 1',
  37:'Graham – Region 10',38:'Granville – Region 4',39:'Greene – Region 2',40:'Guilford – Region 5',
  41:'Halifax – Region 2',42:'Harnett – Region 4',43:'Haywood – Region 9',44:'Henderson – Region 9',
  45:'Hertford – Region 1',46:'Hoke – Region 3',47:'Hyde – Region 1',48:'Iredell – Region 8',
  49:'Jackson – Region 10',50:'Johnston – Region 4',51:'Jones – Region 2',52:'Lee – Region 4',
  53:'Lenoir – Region 2',54:'Lincolnton – Region 8',55:'Macon – Region 10',56:'Madison – Region 9',
  57:'Martin – Region 2',58:'McDowell – Region 8',59:'Mecklenburg – Region 6',60:'Mitchell – Region 9',
  61:'Montgomery – Region 6',62:'Moore – Region 4',63:'Nash – Region 2',64:'New Hanover – Region 3',
  65:'Northhampton – Region 1',66:'Onslow – Region 2',67:'Orange – Region 4',68:'Pamlico – Region 2',
  69:'Pasquotank – Region 1',70:'Pender – Region 3',71:'Perquimans – Region 1',72:'Person – Region 4',
  73:'Pitt – Region 2',74:'Polk – Region 8',75:'Randolph – Region 5',76:'Richmond – Region 6',
  77:'Robeson – Region 3',78:'Rockingham – Region 5',79:'Rowan – Region 5',80:'Rutherford – Region 8',
  81:'Sampson – Region 3',82:'Scotland – Region 6',83:'Stanly – Region 6',84:'Stokes – Region 5',
  85:'Surry – Region 7',86:'Swain – Region 10',87:'Transylvania – Region 9',88:'Tyrrell – Region 1',
  89:'Union – Region 6',90:'Vance – Region 4',91:'Wake – Region 4',92:'Warren – Region 4',
  93:'Washington – Region 1',94:'Watauga – Region 7',95:'Wayne – Region 2',96:'Wilkes – Region 7',
  97:'Wilmington – Region 3',98:'Wilson – Region 2',99:'Yadkin – Region 7',100:'Yancey – Region 9',
  101:'Region 11',
};

function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
    .replace(/"/g,'&quot;').replace(/'/g,'&#039;');
}

function formatDate(val) {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/** yyyy-MM-dd from profile/admin — avoids UTC-parse day shift */
function formatUserDob(val) {
  if (!val) return '';
  const s = String(val).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    const local = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (!isNaN(local.getTime())) {
      return local.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
  }
  return formatDate(val);
}

function isTrue(v) { return v === true || v === 1 || v === '1' || v === 'true'; }
function yesNo(v)  { return isTrue(v) ? 'Yes' : 'No'; }

function imageToDataUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext('2d').drawImage(img, 0, 0);
      try { resolve(c.toDataURL('image/png')); } catch(e) { reject(e); }
    };
    img.onerror = () => reject(new Error('load failed: ' + url));
    img.src = url;
  });
}

// ─── main export ───────────────────────────────────────────────────────────

export async function printUserProfile(userId, token) {
  const cfg = { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };

  try {
    const coreResp = await axios.get(`/api/users/alluserinfo/${userId}`, cfg);
    const user = coreResp.data.user || {};
    const s    = coreResp.data.userSettings || {};

    const guid = user.UserGUID;

    const [skillsListResp, userSkillsResp, eduResp, refResp, contactsResp, certsResp] = await Promise.all([
      axios.get('/api/basic/BP_SkillsQuailifications', cfg).catch(() => ({ data: [] })),
      guid ? axios.get(`/api/skills/${guid}`,     cfg).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      guid ? axios.get(`/api/educations/${guid}`, cfg).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      guid ? axios.get(`/api/references/${guid}`, cfg).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      guid ? axios.get(`/api/contacts/${guid}`,   cfg).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      axios.get('/api/my-certifications', cfg).catch(() => ({ data: [] })),
    ]);

    const skillsMaster = skillsListResp.data || [];
    const userSkills   = userSkillsResp.data || [];
    const educations   = eduResp.data        || [];
    const references   = refResp.data        || [];
    const contacts       = contactsResp.data   || [];
    const certifications = Array.isArray(certsResp.data) ? certsResp.data : [];

    const skillMap = {};
    skillsMaster.forEach(sk => { skillMap[sk.ItemID] = sk; });

    let logoDataUrl = '';
    try { logoDataUrl = await imageToDataUrl(`${window.location.origin}/Images/baptists-on-mission-logo.png`); } catch(e) {}

    openProfilePrintWindow({ user, s, userSkills, skillMap, educations, references, contacts, certifications, logoDataUrl });

  } catch(err) {
    console.error('printUserProfile error:', err);
    alert('Failed to load profile data. Please try again.');
  }
}

// ─── HTML builder ──────────────────────────────────────────────────────────

function openProfilePrintWindow({ user, s, userSkills, skillMap, educations, references, contacts, certifications, logoDataUrl }) {
  const certsList = Array.isArray(certifications) ? certifications : [];
  const win = window.open('', '_blank', 'width=860,height=1100');
  if (!win) { alert('Please allow popups to print the profile.'); return; }

  const fullName    = `${user.FirstName || ''} ${user.LastName || ''}`.trim();
  const memberId    = escapeHtml(String(user.UserID || ''));
  const memberSince = formatDate(user.UserCreated);
  const email       = escapeHtml(user.Email || user.UserName || '');

  const gender = s.UserGender === 1 || s.UserGender === '1' ? 'Male'
               : s.UserGender === 0 || s.UserGender === '0' ? 'Female' : '';

  const regionLabel = s.BP_Regions ? (REGION_MAP[Number(s.BP_Regions)] || String(s.BP_Regions)) : '';
  const stateLabel  = formatUsStateForPrint(s.BP_G_AddressState);

  const logoHtml = logoDataUrl
    ? `<img src="${logoDataUrl}" style="height:60px;width:auto;" alt="Baptists on Mission"/>`
    : `<div style="font-weight:900;font-size:18px;color:#003063;line-height:1.1;">BAPTISTS ON<br/>MISSION</div>`;

  // ── section / row helpers ──
  const SEC = (title) => `
    <tr class="section-row">
      <td colspan="2" class="section-title">${escapeHtml(title)}</td>
    </tr>`;

  const R = (label, value, shade) => {
    const v = (value === null || value === undefined || value === '') ? '' : String(value);
    return `<tr class="${shade ? 'shade' : ''}">
      <td class="lbl">${escapeHtml(label)}</td>
      <td class="val">${escapeHtml(v)}</td>
    </tr>`;
  };

  const TH = (...cols) => `<tr class="th-row">${cols.map(c=>`<td class="th">${escapeHtml(c)}</td>`).join('')}</tr>`;
  const TD = (shade, ...cols) => `<tr class="${shade?'shade':''}">
    ${cols.map(c=>`<td class="val">${escapeHtml(String(c===null||c===undefined?'':c))}</td>`).join('')}
  </tr>`;

  // ── GENERAL ──
  const generalRows = `
    ${SEC('General')}
    ${R('First Name',       user.FirstName || '',   true)}
    ${R('Last Name',        user.LastName  || '',   false)}
    ${R('Date of Birth',    formatUserDob(user.BP_DOB),true)}
    ${R('Gender',           gender,                 false)}
    ${R('Marital Status',   s.BP_G_Marital || '',   true)}
    ${R('Background Check Date', s.BP_BGCheckDate ? formatDate(s.BP_BGCheckDate) : 'Not on record', false)}
    ${R('Phone Number',     s.BP_G_Mob_Phone || '', false)}
    ${R('County / Region',  regionLabel,            true)}
    ${R('Address',          [s.BP_G_Address, s.BP_G_AddressLine2].filter(Boolean).join(', '), false)}
    ${R('City',             s.BP_G_AddressCity || '',  true)}
    ${R('State',            stateLabel,               false)}
    ${R('ZIP Code',         s.BP_G_AddressZip || '',  true)}
    ${R('Username / Email', email,                    false)}
  `;

  // ── CERTIFICATIONS ──
  const certBodyRows = certsList.length > 0
    ? certsList.map((c, i) => {
        const name = (c.Certification || '').trim();
        const expStr = c.Expiration ? formatDate(c.Expiration) : '—';
        const isExpired = String(c.Status || '').toLowerCase() === 'expired';
        const badge = isExpired
          ? '<span class="cert-badge cert-badge-expired">Expired</span>'
          : '<span class="cert-badge cert-badge-active">Active</span>';
        const shade = i % 2 === 0 ? 'shade' : '';
        return `<tr class="${shade}">
          <td class="val">${escapeHtml(name)}</td>
          <td class="val">${escapeHtml(expStr)}</td>
          <td class="val cert-status-cell">${badge}</td>
        </tr>`;
      }).join('')
    : '<tr><td colspan="3" class="empty-row">No certifications on record.</td></tr>';

  const certificationsSection = `
    ${SEC('Certifications')}
    <tr><td colspan="2" style="padding:0;">
      <table class="inner-tbl inner-tbl-certs">
        ${TH('Certification', 'Expiration Date', 'Status')}
        ${certBodyRows}
      </table>
    </td></tr>`;

  // ── SKILLS ──
  let skillRows = userSkills.length > 0
    ? userSkills.map((sk, i) => {
        const master = skillMap[sk.BP_S_Skill] || {};
        return TD(i%2===0, master.Skill||'', master.SkillCategory||'', sk.BP_S_Years||'', sk.BP_S_License||'N/A', sk.BP_S_Status||'');
      }).join('')
    : `<tr><td colspan="5" class="empty-row">No skills on record.</td></tr>`;

  const skillsSection = `
    ${SEC('Skills')}
    <tr><td colspan="2" style="padding:0;">
      <table class="inner-tbl">
        ${TH('Skill','Category','Years of Experience','License Number','Status')}
        ${skillRows}
      </table>
    </td></tr>`;

  // ── EDUCATION ──
  let eduRows = educations.length > 0
    ? educations.map((e, i) => TD(i%2===0, e.BP_E_School||'', e.BP_E_Degree||'', e.BP_E_Field||'', e.BP_E_YearsFrom||'', e.BP_E_YearsTo||'')).join('')
    : `<tr><td colspan="5" class="empty-row">No education records.</td></tr>`;

  const educationSection = `
    ${SEC('Education')}
    <tr><td colspan="2" style="padding:0;">
      <table class="inner-tbl">
        ${TH('School','Degree','Field of Study','Start Year','End Year')}
        ${eduRows}
      </table>
    </td></tr>`;

  // ── REFERENCES ──
  let refRows = references.length > 0
    ? references.map((r, i) => TD(i%2===0, r.BP_R_Name||'', r.BP_R_Phone||'')).join('')
    : `<tr><td colspan="2" class="empty-row">No references on record.</td></tr>`;

  const referencesSection = `
    ${SEC('References')}
    <tr><td colspan="2" style="padding:0;">
      <table class="inner-tbl">
        ${TH('Name','Phone Number')}
        ${refRows}
      </table>
    </td></tr>`;

  // ── EMERGENCY CONTACTS ──
  let contactRows = contacts.length > 0
    ? contacts.map((c, i) => TD(i%2===0, c.BP_EC_FullName||'', c.BP_EC_Relationship||'', c.BP_EC_Email||'', c.BP_EC_Phone||'')).join('')
    : `<tr><td colspan="4" class="empty-row">No emergency contacts on record.</td></tr>`;

  const contactsSection = `
    ${SEC('Emergency Contacts')}
    <tr><td colspan="2" style="padding:0;">
      <table class="inner-tbl">
        ${TH('Name','Relationship','Email','Phone')}
        ${contactRows}
      </table>
    </td></tr>`;

  // ── HEALTH ──
  const medVal      = isTrue(s.BP_H_Medication)  ? (s.BP_H_Medication_Reason  ? `Yes – ${s.BP_H_Medication_Reason}`  : 'Yes') : 'No';
  const allergyVal  = isTrue(s.BP_H_Allergies)    ? (s.BP_H_Allergies_Reason   ? s.BP_H_Allergies_Reason  : 'Yes') : 'None reported';
  const liftingVal  = isTrue(s.BP_H_Lifting)      ? (s.BP_H_Lifting_Reason     ? `Yes – ${s.BP_H_Lifting_Reason}`    : 'Yes') : 'No';
  const ailmentsVal = isTrue(s.BP_H_Ailments)     ? (s.BP_H_Ailments_Reason    ? s.BP_H_Ailments_Reason  : 'Yes') : 'None reported';
  const tetanusVal  = isTrue(s.BP_H_Tetanus)      ? (s.BP_H_Tetanus_Year       ? `Yes – ${s.BP_H_Tetanus_Year}`      : 'Yes') : 'No';
  const alcVal      = isTrue(s.BP_H_Abstain_Alc)  ? 'Yes – Agreed' : 'No';
  const tobVal      = isTrue(s.BP_H_Abstain_Tob)  ? 'Yes – Agreed' : 'No';
  const bpMedVal    = isTrue(s.BP_H_BloodPressure_Med) ? (s.BP_H_BloodPressure_Med_Other ? `Yes – ${s.BP_H_BloodPressure_Med_Other}` : 'Yes') : 'No';
  const heartEvents = s.BP_H_HeartDisease_Events || '';
  const breathVal   = isTrue(s.BP_H_BreathingIssues) ? (s.BP_H_BreathingIssues_Items || 'Yes') : 'No';
  const disabVal    = isTrue(s.BP_H_Disabilities)    ? (s.BP_H_Disabilities_Reason ? `Yes – ${s.BP_H_Disabilities_Reason}` : 'Yes') : 'No';

  const healthRows = `
    ${SEC('Health')}
    ${R('Takes Medication',                medVal,                          true)}
    ${R('Allergies / Dietary Restrictions',allergyVal,                     false)}
    ${R('Lifting Restrictions',            liftingVal,                      true)}
    ${R('Chronic Ailments',                ailmentsVal,                    false)}
    ${R('Tetanus Shot',                    tetanusVal,                      true)}
    ${R('High Blood Pressure',             yesNo(s.BP_H_BloodPressure),    false)}
    ${R('BP Medication',                   bpMedVal,                        true)}
    ${R('History of Heart Disease',        yesNo(s.BP_H_HeartDisease),     false)}
    ${R('Heart Attack',                    heartEvents==='Heart Attack'              ?'Yes':'No', true)}
    ${R('Stent Placement',                 heartEvents==='Stent Placement'           ?'Yes':'No', false)}
    ${R('Open Heart / Bypass Surgery',     heartEvents==='Open Heart Surgery/Bypass' ?'Yes':'No', true)}
    ${R('Valve Surgery',                   heartEvents==='Valve Surgery'             ?'Yes':'No', false)}
    ${R('Diabetes',                        yesNo(s.BP_H_Diabetes),          true)}
    ${R('Oral Medication / Insulin',       yesNo(s.BP_H_Diabetes_OralMed), false)}
    ${R('EPI Pen',                         yesNo(s.BP_H_EPIPen),            true)}
    ${R('History of Seizures',             yesNo(s.BP_H_Seizures),         false)}
    ${R('Breathing Device',                breathVal,                        true)}
    ${R('Any Disabilities',                disabVal,                        false)}
    ${R('Abstain from Alcohol / Drugs',    alcVal,                           true)}
    ${R('Abstain from Tobacco',            tobVal,                          false)}
  `;

  // ── INSURANCE ──
  const beneName = [s.BP_I_FName, s.BP_I_MName, s.BP_I_LName].filter(Boolean).join(' ');
  const insuranceRows = `
    ${SEC('Insurance')}
    <tr><td colspan="2" class="legal-text">
      LIABILITY RELEASE: As a volunteer, I understand the inherent risks of travel and project participation.
      I hereby absolve all sponsoring Baptist bodies and hold them harmless from any claim or demand.
    </td></tr>
    ${R('Has Health Insurance',             yesNo(s.BP_I_Health),                        true)}
    ${R('Supplemental Insurance Accepted',  isTrue(s.BP_I_Agree) ? 'Yes – Agreed' : 'No', false)}
    ${R('Beneficiary Name',                 beneName,                                     true)}
    ${R('Beneficiary Relationship',         s.BP_I_Relation || '',                       false)}
  `;

  // ── PASSPORT ──
  // Filter out the "(select)" placeholder stored when user never picked a gender
  const passportGender = (s.BP_P_Gender && s.BP_P_Gender !== '(select)') ? s.BP_P_Gender : '';
  const passportRows = `
    ${SEC('Passport')}
    ${R('Passport Number',    s.BP_P_Number      || '', true)}
    ${R('Given Names',        s.BP_P_GivenName   || '', false)}
    ${R('Surname',            s.BP_P_Surname     || '', true)}
    ${R('Nationality',        s.BP_P_Nationality || '', false)}
    ${R('Date of Birth',      formatDate(s.BP_P_DOB),   true)}
    ${R('State of Birth',     s.BP_P_POB         || '', false)}
    ${R('Gender',             passportGender,            true)}
    ${R('Date of Issue',      formatDate(s.BP_P_Date_Issued), false)}
    ${R('Date of Expiration', formatDate(s.BP_P_Date_Exp),    true)}
    ${R('Profession',         s.BP_P_Profession  || '', false)}
    ${R('Closest Airport',    s.BP_P_AirportName || '', true)}
  `;

  // ── CHURCH ──
  const churchState = formatUsStateForPrint(s.BP_C_ChurchState);
  const churchRows = `
    ${SEC('Church')}
    ${R('Attends NC Baptist Church', yesNo(s.BP_C_Baptist), true)}
    ${R('Church Name',  s.BP_C_ChurchName  || '', false)}
    ${R('Church City',  s.BP_C_ChurchCity  || '', true)}
    ${R('Church State', churchState,              false)}
    ${R('Church Phone', s.BP_C_ChurchPhone || '', true)}
  `;

  // ── footer name for @page (strip HTML entities for CSS string) ──
  const footerName = fullName.replace(/'/g, "\\'");

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Volunteer Profile – ${escapeHtml(fullName)}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size:12px; color:#111; background:#fff; }
  .page  { max-width:780px; margin:0 auto; padding:32px 32px 60px; }

  /* ── header ── */
  .doc-header { display:flex; align-items:center; gap:28px; padding-bottom:20px; margin-bottom:20px; }
  .doc-name   { font-size:26px; font-weight:700; margin-bottom:3px; }
  .doc-sub    { font-size:12px; color:#555; }

  /* ── main table ── */
  .profile-tbl { width:100%; border-collapse:collapse; }
  .profile-tbl td { border-bottom:1px solid #e4e4e4; }

  .section-row .section-title {
    padding:18px 0 5px;
    font-size:15px;
    font-weight:700;
    color:#111;
    border-bottom:1px solid #bbb;
  }

  .lbl { padding:8px 12px; color:#555; width:220px; vertical-align:top; font-size:12px; }
  .val { padding:8px 12px; color:#111; font-size:12px; font-weight:500; vertical-align:top; }
  tr.shade { background:#f5f5f5; }

  .legal-text { padding:10px 12px; font-size:11px; color:#555; font-style:italic; }

  /* ── inner tables ── */
  .inner-tbl { width:100%; border-collapse:collapse; }
  .inner-tbl td { padding:7px 10px; border-bottom:1px solid #e4e4e4; font-size:12px; }
  .th-row { background:#ebebeb; }
  .th     { font-weight:700; font-size:11px; text-transform:uppercase; color:#555; padding:7px 10px; }
  .empty-row { padding:10px 12px; color:#999; font-style:italic; }

  .cert-badge {
    display:inline-block;
    padding:5px 14px;
    border-radius:999px;
    font-size:11px;
    font-weight:700;
    letter-spacing:0.02em;
  }
  .cert-badge-expired { background:#c62828; color:#fff; }
  .cert-badge-active { background:#2e7d32; color:#fff; }
  .inner-tbl-certs .cert-status-cell { text-align:left; vertical-align:middle; }

  /* ── screen footer ── */
  .page-footer {
    position:fixed; bottom:0; left:0; right:0;
    border-top:1px solid #ccc;
    padding:5px 32px;
    font-size:10px; color:#777;
    display:flex; justify-content:space-between;
    background:#fff;
  }

  /* ── print ── */
  @media print {
    * { -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }
    body { background:#fff; }
    .page { padding:0; max-width:100%; }
    .page-footer { display:none; }

    @page {
      size:A4;
      margin:15mm 15mm 20mm;

      @bottom-left {
        content: "${footerName} \\00b7 Volunteer ID: ${memberId} \\00b7 Baptists On Mission";
        font-family: Arial, Helvetica, sans-serif;
        font-size: 9pt;
        color: #777;
        border-top: 1px solid #ccc;
        padding-top: 4pt;
      }

      @bottom-right {
        content: "Page " counter(page) " of " counter(pages);
        font-family: Arial, Helvetica, sans-serif;
        font-size: 9pt;
        color: #777;
        border-top: 1px solid #ccc;
        padding-top: 4pt;
      }
    }
  }
</style>
</head>
<body>
<div class="page">

  <div class="doc-header">
    ${logoHtml}
    <div>
      <div class="doc-name">${escapeHtml(fullName)}</div>
      <div class="doc-sub">Member Since: ${escapeHtml(memberSince)}&nbsp;&nbsp;&nbsp;Volunteer ID: ${memberId}</div>
    </div>
  </div>

  <table class="profile-tbl">
    ${generalRows}
    ${certificationsSection}
    ${skillsSection}
    ${educationSection}
    ${referencesSection}
    ${contactsSection}
    ${healthRows}
    ${insuranceRows}
    ${passportRows}
    ${churchRows}
  </table>

</div>

<div class="page-footer">
  <span>${escapeHtml(fullName)} &nbsp;·&nbsp; Volunteer ID: ${memberId} &nbsp;·&nbsp; Baptists On Mission</span>
</div>

<script>
  setTimeout(function(){ window.print(); }, 450);
</script>
</body>
</html>`;

  win.document.write(html);
  win.document.close();
}
